// A building's roof condition history, inside Building readiness's roof area: where the
// roof stands, the report it rests on, the remediation as its own thread, the feedback each
// party owes and every exchange, latest first. The state and tiles come from the backend
// (roof_history.card_payload). See docs/Y2026-Q4/design-roof-survey-history.md.
import type {
  RoofDefect,
  RoofHistory,
  RoofReport,
} from "../api/hooks/useProjectAgentStatus";
import { formatDay } from "../utils/attributeFormat";
import styles from "./EmailHistory.module.css";
import {
  Acceptances,
  BriefLine,
  ExchangesSection,
  FeedbackSection,
  FeedbackTile,
  StateLine,
  Tile,
  type Cite,
} from "./ReadinessHistoryParts";

const SOURCE_LABEL: Record<RoofReport["source"], string> = {
  ours: "Our installer or roofer",
  client: "Client's assessor",
  landlord: "Landlord's surveyor",
  independent: "Independent roofer",
};

const PHASE_LABEL: Record<RoofReport["phase"], string> = {
  before_install: "Before install",
  after_works: "After repairs",
  after_install: "After install",
};

const METHOD_LABEL: Record<RoofReport["methods"][number], string> = {
  walked: "walked",
  drone: "drone",
  leak_test: "electronic leak test",
  desk: "desk review",
};

const SEEN_LABEL: Record<RoofDefect["seen_by"], string> = {
  observed: "Seen by the author",
  site: "Reported by the site",
  landlord: "Reported by the landlord",
  client: "Reported by the client",
  not_stated: "",
};

const REPAIRS_LABEL: Record<RoofReport["repairs_before_pv"], string> = {
  none: "No repairs before PV",
  required: "Repairs before PV",
  not_stated: "Repairs before PV not stated",
};

const WARRANTY_LABEL: Record<RoofReport["warranty"]["exists"], string> = {
  yes: "Warranty",
  no: "No warranty",
  not_stated: "",
};

/** The remediation's stages in the order it moves, as the stepper shows them. */
const STAGES: [string, string][] = [
  ["remediation_scoped", "Scoped"],
  ["repair_quoted", "Quoted"],
  ["approval_requested", "With approver"],
  ["remediation_approved", "Approved"],
  ["works_done", "Works done"],
  ["reinspected", "Re-inspected"],
];

const Defects = ({ defects }: { defects: RoofDefect[] }) =>
  defects.length === 0 ? (
    <span className={styles.notRecorded}>None stated</span>
  ) : (
    <ul className={styles.points}>
      {defects.map((d, i) => (
        <li key={i}>
          {d.what}
          {d.where && ` (${d.where})`}
          {SEEN_LABEL[d.seen_by] && (
            <span className={styles.note}>{SEEN_LABEL[d.seen_by]}</span>
          )}
          {d.timing === "before_pv" && (
            <span className={`${styles.tag} ${styles.works}`}> Before PV</span>
          )}
        </li>
      ))}
    </ul>
  );

export const RoofHistoryCard = ({
  history,
  cite,
}: {
  history: RoofHistory;
  cite: Cite;
}) => {
  const rem = history.remediation;
  // A refusal stops at the approver; a temporary repair stops short of works done.
  const at = rem
    ? ({ remediation_declined: "approval_requested", temporary: "remediation_approved" }[
        rem.stage
      ] ?? rem.stage)
    : "";
  const reached = STAGES.findIndex(([stage]) => stage === at);
  return (
    <>
      <StateLine state={history.state} />
      <div className={`${styles.stages} ${styles.tiles}`}>
        <Tile
          label="Report relied on"
          main={history.relied_on && formatDay(history.relied_on.date)}
          empty="No complete report yet"
          sub={
            history.relied_on && (
              <>
                {history.relied_on.version}, {cite(history.relied_on.email)}
                {history.acceptance
                  ? `; accepted ${formatDay(history.acceptance.date)}`
                  : "; not accepted"}
              </>
            )
          }
        />
        <Tile
          label="Remediation"
          main={rem?.label}
          empty="Nothing logged"
          sub={rem?.entry && <BriefLine brief={rem.entry} cite={cite} />}
        />
        <Tile
          label="Roof ready"
          main={history.ready && formatDay(history.ready.date)}
          empty="Not yet"
          sub={history.ready && <BriefLine brief={history.ready} cite={cite} />}
        />
        <Tile
          label="After install"
          main={
            history.after_install.accepted
              ? formatDay(history.after_install.accepted.date)
              : history.after_install.report
                ? "Report issued"
                : ""
          }
          empty="Not started"
          sub={
            history.after_install.report &&
            !history.after_install.accepted &&
            "Waiting for the client's acceptance"
          }
        />
        <FeedbackTile history={history} />
      </div>

      {history.disagree.length > 0 && (
        <div className={styles.dispute} role="alert">
          <strong className={styles.disputeHead}>Reports disagree</strong>
          <p className={styles.disputeText}>
            These reports differ on whether repairs are needed before PV. The
            roof still rests on the last accepted one.
          </p>
          <ul className={styles.disputeList}>
            {history.disagree.map((v) => (
              <li key={v.entry_id}>
                <strong>{v.version}</strong>, issued {formatDay(v.date)}.{" "}
                {cite(v.email)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.section}>
        <h4 className={styles.sectionLabel}>Report versions</h4>
        {history.versions.length === 0 ? (
          <p className={styles.empty}>No roof report logged yet.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Issued</th>
                  <th>Version</th>
                  <th>Condition</th>
                  <th>Defects (who saw them)</th>
                  <th>Life and warranty</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {history.versions.map((v) => (
                  <tr key={v.entry_id}>
                    <td
                      className={`${styles.date} ${v.relied_on ? styles.first : ""}`}
                    >
                      {formatDay(v.date)}
                    </td>
                    <td>
                      <strong>
                        {v.report.firm} {v.report.revision}
                      </strong>
                      {v.report.status === "draft" && (
                        <span className={`${styles.tag} ${styles.other}`}>
                          {" "}
                          Draft
                        </span>
                      )}
                      <span className={styles.note}>
                        {SOURCE_LABEL[v.report.source]} ·{" "}
                        {PHASE_LABEL[v.report.phase]}
                        {v.report.methods.length > 0 &&
                          ` · ${v.report.methods.map((m) => METHOD_LABEL[m]).join(", ")}`}
                      </span>
                      {v.relied_on && (
                        <span className={styles.note}>Relied on</span>
                      )}
                      <Acceptances accepted={v.accepted} />
                      {v.report.document && (
                        <span className={styles.note}>
                          {cite(v.report.document)}
                        </span>
                      )}
                    </td>
                    <td>
                      {v.report.rating || (
                        <span className={styles.notRecorded}>
                          Not stated in the report
                        </span>
                      )}
                      <span className={styles.note}>
                        {REPAIRS_LABEL[v.report.repairs_before_pv]}
                      </span>
                      {v.report.recommendation && (
                        <span className={styles.note}>
                          Recommends {v.report.recommendation}
                        </span>
                      )}
                    </td>
                    <td className={styles.summary}>
                      <Defects defects={v.report.defects} />
                    </td>
                    <td>
                      {v.report.remaining_life ? (
                        `Life ${v.report.remaining_life}`
                      ) : (
                        <span className={styles.notRecorded}>
                          Life not stated
                        </span>
                      )}
                      {WARRANTY_LABEL[v.report.warranty.exists] && (
                        <span className={styles.note}>
                          {WARRANTY_LABEL[v.report.warranty.exists]}
                          {v.report.warranty.holder &&
                            ` (${v.report.warranty.holder})`}
                          {v.report.warranty.effect &&
                            `: ${v.report.warranty.effect}`}
                        </span>
                      )}
                    </td>
                    <td>{cite(v.email)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h4 className={styles.sectionLabel}>Remediation</h4>
        {!rem || !rem.scope ? (
          <p className={styles.empty}>
            {rem?.stage === "not_needed"
              ? "Not needed: an accepted report says no repairs before PV."
              : rem?.stage === "needed"
                ? "The report relied on needs repairs before PV; nothing is scoped yet."
                : "No remediation logged."}
          </p>
        ) : (
          <>
            <p className={styles.prose}>
              Now: <strong>{rem.label}</strong>
            </p>
            <ol className={styles.stepper}>
              {STAGES.map(([stage, label], i) => (
                <li
                  key={stage}
                  className={
                    i <= reached ? styles.stepperDone : styles.stepperTodo
                  }
                >
                  {label}
                </li>
              ))}
            </ol>
            <p className={styles.prose}>
              <strong>{rem.scope.what}</strong>
              {rem.scope.zone && `, ${rem.scope.zone}`}
              {rem.scope.by && `; done by ${rem.scope.by}`}
              {rem.scope.pays && `; paid by ${rem.scope.pays}`}
              {rem.route && `; approved through ${rem.route}`}
            </p>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>What</th>
                    <th>From</th>
                    <th>What it shows</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {rem.thread.map((e) => (
                    <tr key={e.entry_id}>
                      <td className={styles.date}>{formatDay(e.date)}</td>
                      <td>
                        <span className={`${styles.tag} ${styles.remediation}`}>
                          {e.kind_label}
                        </span>
                      </td>
                      <td>{e.sender}</td>
                      <td className={styles.summary}>{e.summary}</td>
                      <td>{cite(e.email)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <FeedbackSection history={history} cite={cite} />
      <ExchangesSection
        entries={history.entries}
        cite={cite}
        empty="Building readiness has not logged any message about the roof yet."
      />
    </>
  );
};
